/**
 * Format- und Ampel-Logik — der harte Kern gegen Fehldarstellung von null.
 * Quelle: SCHEMA_MAPPING §4 (Ampel) + §5 (null-Handling).
 *
 * HARTE REGELN:
 *  - null NIEMALS als 0 rendern. null → "—" bzw. "geplant" (SOLL-only-Kontext).
 *  - abweichung_pct == null → graue/keine Ampel, NIE grün/rot.
 */

/** Platzhalter für fehlende Werte (null-IST). */
export const DASH = '—';

const numberFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const eurFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const pctFormatter = new Intl.NumberFormat('de-DE', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export type FormatKind = 'number' | 'eur' | 'percent';

export interface FormatOptions {
  kind?: FormatKind;
  /** Text, der für null ausgegeben wird. Default "—". Z.B. "geplant" im SOLL-Kontext. */
  nullText?: string;
}

/**
 * formatValue — die EINE Funktion für jede Wertanzeige.
 * null/undefined/NaN → nullText (Default "—"), NIEMALS 0.
 * Eine echte 0 wird als "0" (bzw. "0 €" / "0,0 %") gerendert — klar getrennt von null.
 */
export function formatValue(v: number | null | undefined, opts: FormatOptions = {}): string {
  const nullText = opts.nullText ?? DASH;
  if (v === null || v === undefined || Number.isNaN(v)) {
    return nullText;
  }
  switch (opts.kind) {
    case 'eur':
      return eurFormatter.format(v);
    case 'percent':
      return pctFormatter.format(v);
    case 'number':
    default:
      return numberFormatter.format(v);
  }
}

/** Bequemer Shortcut für EUR-Werte. */
export function formatEur(v: number | null | undefined, nullText?: string): string {
  return formatValue(v, { kind: 'eur', nullText });
}

/** Bequemer Shortcut für Prozentwerte (erwartet Dezimal, z.B. -0.04 → "-4,0 %"). */
export function formatPct(v: number | null | undefined, nullText?: string): string {
  return formatValue(v, { kind: 'percent', nullText });
}

// ---------------------------------------------------------------------------
// Ampel-Logik (SCHEMA_MAPPING §4)
// ---------------------------------------------------------------------------

export type AmpelStatus = 'green' | 'yellow' | 'red' | 'gray';

/**
 * [Annahme] Ampel-Schwellen — VON TOM-ERIC ZU BESTÄTIGEN (OD-4, SCHEMA_MAPPING §4).
 * NICHT als fachlich gesetzt behandeln. Konfigurierbar gehalten, damit eine
 * spätere Bestätigung nur diese Konstante ändert, nicht die Logik.
 *
 * Gilt sauber nur für Ertrags-/Ergebnis-KPIs (NKM, EvV, EBTDA, JÜ).
 * Vorzeichen-Konvention für Aufwandszeilen ist OFFEN (§4) — daher hier
 * bewusst KEINE Aufwands-Sonderlogik; Aufrufer sollte die Ampel vorerst nur
 * für Ergebnis-KPIs nutzen.
 */
export const AMPEL_THRESHOLDS = {
  /** abw >= greenMin → grün */
  greenMin: -0.02,
  /** greenMin > abw >= yellowMin → gelb; abw < yellowMin → rot */
  yellowMin: -0.1,
} as const;

/**
 * ampelStatus — wandelt abweichung_pct in einen Ampel-Status.
 *  - null/undefined/NaN → 'gray' (kein IST → KEINE Ampel; NIE grün/rot).
 *  - >= greenMin (-0.02) → 'green'
 *  - >= yellowMin (-0.10) → 'yellow'
 *  - sonst → 'red'
 */
export function ampelStatus(
  abweichungPct: number | null | undefined,
  thresholds: { greenMin: number; yellowMin: number } = AMPEL_THRESHOLDS,
): AmpelStatus {
  if (abweichungPct === null || abweichungPct === undefined || Number.isNaN(abweichungPct)) {
    return 'gray';
  }
  if (abweichungPct >= thresholds.greenMin) return 'green';
  if (abweichungPct >= thresholds.yellowMin) return 'yellow';
  return 'red';
}

/** Barrierefreies Text-Label je Status (für aria-label / Tooltip). */
export const AMPEL_LABELS: Record<AmpelStatus, string> = {
  green: 'im Rahmen',
  yellow: 'leichte Unterschreitung',
  red: 'deutliche Unterschreitung',
  gray: 'kein Ist-Wert (geplant)',
};

/**
 * Portfolio liefert kein abweichung_pct (§2.5) — Frontend rechnet ist/soll-1.
 * null-IST oder soll==0/null → null (KEINE erfundene Abweichung).
 */
export function computeAbweichung(
  ist: number | null | undefined,
  soll: number | null | undefined,
): number | null {
  if (ist === null || ist === undefined || Number.isNaN(ist)) return null;
  if (soll === null || soll === undefined || Number.isNaN(soll) || soll === 0) return null;
  return ist / soll - 1;
}
